import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FaPlus, FaSearch, FaTimes } from "react-icons/fa";
import axios from "axios";
import { API_URL } from "../config";
import { getToken } from "../token";
import { FORUM, TRANSITION } from "../Theme/styles";
import { findCategory } from "../categories";
import { myId } from "../permissions";
import { formatDate, daysWord, parseDate } from "../dates";
import { priorityName } from "../priorities";
import TaskModal from "../Components/TaskModal";

const ProjectStatus = () => {
  const [tasks, setZadania] = useState([]);
  const [loading, setLaduje] = useState(true);
  const [openTask, setOtwarte] = useState(null);
  const [people, setOsoby] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const podzial = useMemo(() => {
    const poTerminie = (a, b) => {
      const x = mojTermin(a) || a.deadline || "9999";
      const y = mojTermin(b) || b.deadline || "9999";
      return String(x).localeCompare(String(y));
    };
    return {
      moje: tasks.filter((z) => mojePrzydzialy(z).length > 0).sort(poTerminie),
      reszta: tasks.filter((z) => mojePrzydzialy(z).length === 0).sort(poTerminie),
    };
  }, [tasks]);

  const headers = { Authorization: `Bearer ${getToken()}` };

  const load = useCallback(() => {
    setLaduje(true);
    axios
      .get(`${API_URL}/project-status`, { withCredentials: true, headers: headers })
      .then((r) => setZadania(r.data || []))
      .catch((e) => console.error("Nie udało się pobrać statusu projektu", e))
      .finally(() => setLaduje(false));
  }, []);

  useEffect(() => {
    load();
    axios
      .get(`${API_URL}/users`, { withCredentials: true, headers: headers })
      .then((r) => setOsoby(r.data || []))
      .catch(() => setOsoby([]));
  }, [load]);

  const unpin = (task) =>
    axios
      .delete(`${API_URL}/project-status/${task.id}`, {
        withCredentials: true,
        headers: headers,
      })
      .then(load)
      .catch((e) => console.error("Nie udało się zdjąć zadania", e));

  return (
    <Flex direction={"column"} h={"100%"} minH={0} gap={4} w={"100%"}>
      <Flex direction={"row"} gap={4} align={"center"}>
        <Flex direction={"column"} gap={0.5}>
          <Text
            fontSize={"11px"}
            fontWeight={"500"}
            letterSpacing={"0.08em"}
            textTransform={"uppercase"}
            color={FORUM.tekstPrzygaszony}
          >
            Zadania główne
          </Text>
          <Text
            fontSize={"xl"}
            fontWeight={"600"}
            lineHeight={"1.1"}
            color={FORUM.tekstJasny}
          >
            Status projektu
          </Text>
        </Flex>
        <Box flex={1} />
        <Button variant={"glowny"} size={"sm"} leftIcon={<FaPlus />} onClick={onOpen}>
          Dodaj zadanie do statusu
        </Button>
      </Flex>

      <Box flex={1} minH={0} overflowY={"auto"}>
        {loading && tasks.length === 0 && (
          <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
            Wczytuję…
          </Text>
        )}
        {!loading && tasks.length === 0 && (
          <Box
            border={"1px dashed"}
            borderColor={FORUM.obramowanie}
            borderRadius={"md"}
            px={4}
            py={8}
          >
            <Text fontSize={"sm"} color={FORUM.tekst} mb={1}>
              Nic tu jeszcze nie stoi.
            </Text>
            <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
              Dodaj zadania, których pilnujesz — na tablicach zostaną tam, gdzie są.
            </Text>
          </Box>
        )}

        {podzial.moje.length > 0 && (
          <>
            <Text
              fontSize={"10px"}
              fontWeight={"600"}
              letterSpacing={"0.08em"}
              textTransform={"uppercase"}
              color={"#6C99FF"}
              mb={1.5}
            >
              Twoje
            </Text>
            <Flex direction={"column"} gap={2} mb={4}>
              {podzial.moje.map((z) => (
                <StatusRow
                  key={z.id}
                  task={z}
                  onOpen={() => setOtwarte(z)}
                  onUnpin={() => unpin(z)}
                />
              ))}
            </Flex>
          </>
        )}

        {podzial.reszta.length > 0 && (
          <>
            {podzial.moje.length > 0 && (
              <Text
                fontSize={"10px"}
                fontWeight={"600"}
                letterSpacing={"0.08em"}
                textTransform={"uppercase"}
                color={FORUM.tekstPrzygaszony}
                mb={1.5}
              >
                Pozostałe
              </Text>
            )}
            <Flex direction={"column"} gap={2}>
              {podzial.reszta.map((z) => (
                <StatusRow
                  key={z.id}
                  task={z}
                  onOpen={() => setOtwarte(z)}
                  onUnpin={() => unpin(z)}
                />
              ))}
            </Flex>
          </>
        )}
      </Box>

      <TaskPickerModal isOpen={isOpen} onClose={onClose} onAdded={load} />

      {openTask && (
        <TaskModal
          isOpen={Boolean(openTask)}
          onClose={() => {
            setOtwarte(null);
            load();
          }}
          taskId={openTask.id}
          peoples={people}
        />
      )}
    </Flex>
  );
};

const mojePrzydzialy = (task) =>
  (task.assignments || []).filter(
    (p) =>
      String(p.assigned_to) === String(myId()) || String(p.claimed_by) === String(myId())
  );

const mojTermin = (task) => {
  const terminy = mojePrzydzialy(task)
    .filter((p) => p.status !== "ukonczone")
    .map((p) => p.deadline)
    .filter(Boolean)
    .sort();
  return terminy[0] || null;
};

const StatusRow = ({ task, onOpen, onUnpin }) => {
  const moje = mojePrzydzialy(task);
  const mojeId = new Set(moje.map((p) => p.id));

  const parts = (task.assignments || []).map((p) => {
    const date = parseDate(p.deadline);
    const late =
      date && p.status !== "ukonczone" && date.getTime() < new Date().setHours(0, 0, 0, 0);
    return {
      key: p.id,
      name: findCategory(p.team)?.name || p.team,
      deadline: p.deadline ? formatDate(p.deadline) : null,
      late,
      mine: mojeId.has(p.id),
    };
  });

  const wiodacyTermin = mojTermin(task) || task.deadline;

  const days = wiodacyTermin
    ? Math.round(
        (new Date(wiodacyTermin).setHours(0, 0, 0, 0) -
          new Date().setHours(0, 0, 0, 0)) /
          86400000
      )
    : null;

  const deadlineColor = task.done_all
    ? FORUM.tekstPrzygaszony
    : days === null
    ? FORUM.tekstPrzygaszony
    : days < 0
    ? "#F08A80"
    : days <= 3
    ? "#E0A45C"
    : FORUM.tekst;

  return (
    <Flex
      align={{ base: "flex-start", sm: "center" }}
      gap={3}
      px={{ base: 3, sm: 4 }}
      py={3}
      bg={FORUM.panel}
      border={"1px solid"}
      borderColor={FORUM.obramowanie}
      borderRadius={"md"}
      cursor={"pointer"}
      transition={TRANSITION}
      _hover={{ bg: FORUM.panelJasny }}
      onClick={onOpen}
    >
      <Box minW={"64px"}>
        <Text fontSize={"sm"} fontWeight={"600"} color={deadlineColor}>
          {wiodacyTermin
            ? new Date(wiodacyTermin).toLocaleDateString("pl-PL", {
                day: "2-digit",
                month: "2-digit",
              })
            : "—"}
        </Text>
        {days !== null && !task.done_all && (
          <Text fontSize={"10px"} color={FORUM.tekstPrzygaszony}>
            {days < 0
              ? `${-days} ${daysWord(days)} po`
              : days === 0
              ? "dzisiaj"
              : `za ${days} ${daysWord(days)}`}
          </Text>
        )}
      </Box>

      <Box flex={1} minW={0}>
        <Text
          fontSize={"sm"}
          fontWeight={"500"}
          color={task.done_all ? FORUM.tekstPrzygaszony : FORUM.tekstJasny}
          textDecoration={task.done_all ? "line-through" : "none"}
          noOfLines={1}
        >
          {task.title}
        </Text>
        <Flex fontSize={"11px"} color={FORUM.tekstPrzygaszony} gap={1.5} wrap={"wrap"}>
          {parts.map((part, i) => (
            <Text as={"span"} key={part.key} whiteSpace={"nowrap"}>
              {i > 0 && <Text as={"span"} mr={1.5}>|</Text>}
              <Text
                as={"span"}
                color={part.mine ? "#6C99FF" : "inherit"}
                fontWeight={part.mine ? "600" : "400"}
              >
                {part.name}
              </Text>
              {part.deadline && (
                <Text
                  as={"span"}
                  color={part.late ? "#F08A80" : part.mine ? "#6C99FF" : FORUM.tekst}
                  fontWeight={part.mine ? "600" : "400"}
                >
                  : {part.deadline}
                </Text>
              )}
            </Text>
          ))}
          <Text as={"span"}>· priorytet {priorityName(task.piority).toLowerCase()}</Text>
        </Flex>
      </Box>

      <Flex align={"center"} gap={2} flexShrink={0}>
        <Box
          display={{ base: "none", sm: "block" }}
          w={"70px"}
          h={"5px"}
          borderRadius={"full"}
          bg={FORUM.plakietka}
          overflow={"hidden"}
        >
          <Box
            h={"100%"}
            w={`${task.items_total ? (task.items_done / task.items_total) * 100 : 0}%`}
            bg={task.done_all ? "#4FB477" : "#6C99FF"}
            transition={TRANSITION}
          />
        </Box>
        <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony} minW={"34px"}>
          {task.items_done}/{task.items_total}
        </Text>
        <Box
          as={"button"}
          onClick={(e) => {
            e.stopPropagation();
            onUnpin();
          }}
          color={FORUM.tekstPrzygaszony}
          transition={TRANSITION}
          _hover={{ color: "#E06C6C" }}
          title={"Zdejmij ze statusu"}
        >
          <FaTimes size={12} />
        </Box>
      </Flex>
    </Flex>
  );
};

const TaskPickerModal = ({ isOpen, onClose, onAdded }) => {
  const [search, setSzukaj] = useState("");
  const [lista, setLista] = useState([]);
  const [selected, setWybrane] = useState(null);
  const [deadline, setTermin] = useState("");
  const [saving, setZapisuje] = useState(false);

  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (!isOpen) return;
    const czasomierz = setTimeout(() => {
      axios
        .get(`${API_URL}/tasks?search=${encodeURIComponent(search)}`, {
          withCredentials: true,
          headers: headers,
        })
        .then((r) => setLista(r.data || []))
        .catch(() => setLista([]));
    }, 250);
    return () => clearTimeout(czasomierz);
  }, [search, isOpen]);

  const save = () => {
    if (!selected || saving) return;
    setZapisuje(true);
    axios
      .put(
        `${API_URL}/project-status/${selected.id}`,
        { deadline: deadline || null },
        { withCredentials: true, headers: headers }
      )
      .then(() => {
        setWybrane(null);
        setTermin("");
        setSzukaj("");
        onAdded();
        onClose();
      })
      .catch((e) => console.error("Nie udało się dodać do statusu", e))
      .finally(() => setZapisuje(false));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "lg" }} scrollBehavior={"inside"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Dodaj zadanie do statusu</ModalHeader>
        <ModalCloseButton mt={1} />
        <ModalBody>
          <Flex align={"center"} gap={2} mb={3}>
            <FaSearch color={FORUM.tekstPrzygaszony} size={12} />
            <Input
              size={"sm"}
              placeholder={"Szukaj zadania po tytule"}
              value={search}
              onChange={(e) => setSzukaj(e.target.value)}
              autoFocus
            />
          </Flex>

          <Flex direction={"column"} gap={1} maxH={"300px"} overflowY={"auto"}>
            {lista.map((z) => (
              <Flex
                key={z.id}
                direction={"column"}
                px={3}
                py={2}
                borderRadius={"md"}
                cursor={"pointer"}
                border={"1px solid"}
                borderColor={selected?.id === z.id ? "#6C99FF" : "transparent"}
                bg={selected?.id === z.id ? "rgba(108,153,255,0.10)" : FORUM.tlo}
                transition={TRANSITION}
                _hover={{ bg: FORUM.panelJasny }}
                onClick={() => {
                  setWybrane(z);
                  setTermin(z.deadline || "");
                }}
              >
                <Text fontSize={"sm"} color={FORUM.tekst} noOfLines={1}>
                  {z.title}
                </Text>
                <Text fontSize={"10px"} color={FORUM.tekstPrzygaszony} noOfLines={1}>
                  {[...new Set(z.teams || [])]
                    .map((k) => findCategory(k)?.name || k)
                    .join(" · ") || "bez zespołu"}
                </Text>
              </Flex>
            ))}
            {lista.length === 0 && (
              <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
                Nic nie matches.
              </Text>
            )}
          </Flex>

          {selected && (
            <Box mt={4}>
              <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony} mb={1.5}>
                Do created_at ma być done_all
              </Text>
              <Input
                type={"date"}
                size={"sm"}
                value={deadline}
                onChange={(e) => setTermin(e.target.value)}
              />
              <Text fontSize={"10px"} color={FORUM.tekstPrzygaszony} mt={1}>
                Ta data staje się terminem tasks — także na tablicach zespołów.
              </Text>
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant={"drugorzedny"} onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant={"glowny"}
            onClick={save}
            isDisabled={!selected}
            isLoading={saving}
          >
            Dodaj do statusu
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProjectStatus;
